import { AppDispatch, RootState } from '@/app/store';
import { CreateLetterDTO } from '@/types/create/CreateLetterDTO';
import { ErrorDTO } from '@/types/ErrorDTO';
import { Grid, Skeleton, Theme, Typography, useMediaQuery } from '@mui/material';
import { InitiativeLetterInfo } from '@/components/letter/initiative/InitiativeLetterInfo';
import { LetterBalance } from '@/components/create/unauthorized/LetterBalance';
import { LetterEmailDTO } from '@/types/letter/LetterEmailDTO';
import { LetterStatus } from '@/types/letter/LetterStatus';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { PaymentMethod } from '@/types/payment/PaymentMethod';
import { PaymentProvider } from '@/types/payment/PaymentProvider';
import { ROUTE_MAIN, ROUTE_VIEW_INITIATIVE } from '@/app/routes';
import { SbpMethod } from '@/types/sbp/SbpMethod';
import { clearFormState, getIdempotencyKey, getLetterNumber } from '@/services/createFormSlice';
import { clearSbpQrState, getSbpPaymentMethod, getSbpPaymentProvider, getSbpQrOutdated } from '@/services/sbpSlice';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetInitiativeLetterQuery, useSetEmailToLetterMutation } from '@/services/api/letterApiSlice';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, ReactElement, useCallback, useEffect } from 'react';

type Params = {
  uuid: string;
};

export const InitiativeLetterPayment: FC = (): ReactElement => {
  const { uuid } = useParams<Params>();
  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const idempotencyKey: string | undefined = useAppSelector(getIdempotencyKey);
  const letterNumber: number | undefined = useAppSelector(getLetterNumber);

  const qrOutdated: boolean = useAppSelector((state: RootState): boolean =>
    getSbpQrOutdated(state, SbpMethod.LETTER, undefined, true, true),
  );

  const paymentMethod: PaymentMethod | undefined = useAppSelector((state: RootState): PaymentMethod | undefined =>
    getSbpPaymentMethod(state, SbpMethod.LETTER, undefined, true, true),
  );

  const paymentProvider: PaymentProvider | undefined = useAppSelector((state: RootState): PaymentProvider | undefined =>
    getSbpPaymentProvider(state, SbpMethod.LETTER, undefined, true, true),
  );

  const {
    data: letter,
    isLoading: isLetterLoading,
    error,
    refetch: getInitiativeLetter,
  } = useGetInitiativeLetterQuery(uuid, { skip: !uuid, refetchOnMountOrArgChange: true });

  const [setEmailToLetter, { isLoading: isEmailLoading }] = useSetEmailToLetterMutation();

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const isInProgress = (): boolean => {
    return Boolean(paymentMethod) && Boolean(paymentProvider);
  };

  const formatPrisonerName = (): string => {
    let prisonerName = '';
    if (letter?.prisonerLastName) {
      prisonerName += letter.prisonerLastName;
    }
    if (letter?.prisonerBirthDate) {
      prisonerName += ', ' + format(letter?.prisonerBirthDate, 'yyyy');
    }
    return prisonerName;
  };

  const formatPhone = (phone?: string): string => {
    return phone?.startsWith('+') ? phone : '+' + phone;
  };

  const createDummyLetter = (): CreateLetterDTO => {
    return {
      number: letter?.number,
      uuid: letter?.uuid,
      author: null,
      sender: null,
      email: letter?.recipientEmail,
    };
  };

  const handleEmailChange = async (email: string): Promise<void> => {
    if (letter?.number) {
      const dto: LetterEmailDTO = {
        number: letter?.number,
        email: email,
      };
      await setEmailToLetter(dto)
        .unwrap()
        .then((): void => {
          getInitiativeLetter();
        })
        .catch((e: { status: number; data: ErrorDTO }): void => {
          enqueueSnackbar(e.data.message, { variant: 'error' });
        });
    }
  };

  const handleSbpQrPaymentSuccess = (): void => {
    getInitiativeLetter();
  };

  const clearState = useCallback((): void => {
    clearSbpQrState(dispatch, SbpMethod.LETTER, undefined, false, true);
    dispatch(clearFormState());
  }, [dispatch]);

  useEffect((): void => {
    if (uuid) {
      if (error) {
        enqueueSnackbar((error as { status: number; data: ErrorDTO }).data.message, { variant: 'error' });
        navigate(ROUTE_MAIN, { replace: true });
      }
    } else {
      navigate(ROUTE_MAIN, { replace: true });
    }
  }, [enqueueSnackbar, error, navigate, uuid]);

  useEffect((): void => {
    if (letter?.status === LetterStatus.PAID_INITIATIVE) {
      enqueueSnackbar('Инициативное письмо оплачено', { variant: 'success' });
      navigate(ROUTE_VIEW_INITIATIVE + '?uuid=' + letter?.uuid, { replace: true });
    }
  }, [enqueueSnackbar, letter?.status, letter?.uuid, navigate]);

  useEffect((): void => {
    if (qrOutdated) {
      clearState();
    }
    if (letter?.number && letterNumber && letter?.number != letterNumber) {
      clearState();
    }
  }, [clearState, dispatch, letter?.number, letterNumber, qrOutdated]);

  useEffect((): (() => void) => {
    if (!!letter && letter?.status === LetterStatus.PAID_INITIATIVE) {
      return (): void => {};
    }
    const timerId: ReturnType<typeof setInterval> = setInterval((): void => {
      getInitiativeLetter();
    }, 30000);
    return (): void => {
      clearInterval(timerId);
    };
  }, [getInitiativeLetter, letter]);

  const isPaymentSuccess: boolean = !!letter && letter?.status === LetterStatus.PAID_INITIATIVE;

  return (
    <Grid container={true} direction={'column'}>
      <Grid item={true} mb={2}>
        <PageHeader
          tittle={
            <Fragment>
              <Grid container={true} direction={'row'} spacing={1}>
                <Grid item={true}>
                  <Typography variant={'inherit'} component={'span'}>
                    {'Оплата инициативного письма'}
                  </Typography>
                </Grid>
                <Grid item={true}>
                  <Grid container={true} direction={'row'} spacing={1} wrap={'nowrap'}>
                    <Grid item={true}>
                      <Typography variant={'inherit'} component={'span'}>
                        {'№'}
                      </Typography>
                    </Grid>
                    <Grid item={true}>
                      <Typography variant={'inherit'} component={'span'}>
                        {isLetterLoading ? <Skeleton width={'136px'} /> : letter?.number}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          }
          isMobile={isMobile}
          mobileTittle={
            <Fragment>
              <Grid container={true} direction={'row'} spacing={1}>
                <Grid item={true}>
                  <Typography variant={'inherit'} component={'span'}>
                    {'Оплата инициативного письма'}
                  </Typography>
                </Grid>
                <Grid item={true}>
                  <Grid container={true} direction={'row'} spacing={1} wrap={'nowrap'}>
                    <Grid item={true}>
                      <Typography variant={'inherit'} component={'span'}>
                        {'№'}
                      </Typography>
                    </Grid>
                    <Grid item={true}>
                      <Typography variant={'inherit'} component={'span'}>
                        {isLetterLoading ? <Skeleton width={'136px'} /> : letter?.number}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          }
        />
      </Grid>
      <Grid item={true} mb={3}>
        <Grid container={true} columnSpacing={'55px'}>
          <Grid item={true} sm={7}>
            <InitiativeLetterInfo
              sender={formatPrisonerName()}
              receiver={letter?.recipientFullName}
              phone={formatPhone(letter?.recipientPhone)}
              email={letter?.recipientEmail}
              onEmailChange={handleEmailChange}
              isMobile={isMobile}
              isEmailConfirmed={!!letter?.recipientEmail}
              isEmailLoading={isEmailLoading}
              isLetterLoading={isLetterLoading}
            />
          </Grid>
          <Grid item={true} sm={5}>
            <LetterBalance
              letter={createDummyLetter()}
              sudokuAttached={false}
              sudokuCount={0}
              crosswordAttached={false}
              crosswordCount={0}
              newsAttached={false}
              sportsNewsAttached={false}
              isLetterSent={isInProgress()}
              isInitiative={true}
              isMobile={isMobile}
              letterCost={letter?.price}
              isInitiativeSubmitAllowed={!!letter?.recipientEmail}
              idempotencyKey={idempotencyKey}
              onSbpQrPaymentSuccess={handleSbpQrPaymentSuccess}
              isPaymentSuccess={isPaymentSuccess}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
